import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IRequestTypeAddPayload, IRequestTypeUpdatePayload } from './cerere-type.interface';
import { 
  CerereTypeFunctieAssignment, 
  FunctieAssignmentRequest,
  FunctieData,
  DirectieData,
  CompartimentData,
  UserData
} from './cerere-type-functie-assignment.interface';
import { catchError, map, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class CerereTypeService {

    private serviceName = 'CerereTypeService';

    private token!: string;
    private basePath = environment.interop.basePath;
    private httpOptions: any;
    private getRequestTypesUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.list;
    private getRequestTypesDTUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.listDT;
    private storeRequestTypeUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.store;
    private updateRequestTypeUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.update;
    private deleteRequestTypeUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.delete;
    private getRequestTypesActiveUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.listActive;
    private getRequestTypesPublicUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.listPublic;
    private changeRequestTypeActiveUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.active;
    private changeRequestTypePublicUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.public;

    private clearCacheUrl = this.basePath + environment.interop.dgaspc.nomenclatoare.cerereType.clearCache;

    constructor(private http: HttpClient) { }

    getRequestTypesDT() {
        return this.http.get(this.getRequestTypesDTUrl, this.httpOptions);
    }

    clearCache() {
        return this.http.get(this.clearCacheUrl, this.httpOptions);
    }

    addRequestType(data: IRequestTypeAddPayload) {
        return this.http.post(this.storeRequestTypeUrl, data, this.httpOptions);
    }

    updateRequestType(data: IRequestTypeUpdatePayload) {
        return this.http.put(this.updateRequestTypeUrl, data, this.httpOptions);
    }

    deleteRequestType(requestTypeId: number) {
        return this.http.delete(this.deleteRequestTypeUrl + requestTypeId);
    }

    getActiveRequestTypes() {
        return this.http.get(this.getRequestTypesActiveUrl, this.httpOptions);
    }

    getRecordsList() {
        return this.http
            .get<{ errors: boolean; data: any[]; status_code: number }>(
                this.getRequestTypesActiveUrl
            )
            .pipe(
                map((response) =>
                    response.data.map((item) => ({
                        id: item.id,
                        name: item.name,
                    }))
                ),
                catchError((error) => {
                    console.error('Error in get request with service ' + this.serviceName, error);
                    return throwError(error);
                })
            );
    }

    getPublicRequestTypes() {
        return this.http.get(this.getRequestTypesPublicUrl, this.httpOptions);
    }

    changeStatusActive(id: number) {
        return this.http.put(this.changeRequestTypeActiveUrl + id, {}, this.httpOptions);
    }

    changeStatusPublic(id: number) {
        return this.http.put(this.changeRequestTypePublicUrl + id, {}, this.httpOptions);
    }

    setToken(token: string) {
        this.token = token;
        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${this.token}`
            })
        };
    }

    // ============================================================================
    // Functie Assignment Management Methods
    // ============================================================================

    /**
     * Get functie assignments for a cerere type
     */
    getFunctieAssignments(cerereTypeId: number): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/${cerereTypeId}/functie-assignments`;
        return this.http.get(url, this.httpOptions);
    }

    /**
     * Update functie assignments for a cerere type
     */
    updateFunctieAssignments(cerereTypeId: number, request: FunctieAssignmentRequest): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/${cerereTypeId}/functie-assignments`;
        return this.http.put(url, request, this.httpOptions);
    }

    /**
     * Delete a specific functie assignment
     */
    deleteFunctieAssignment(cerereTypeId: number, assignmentId: number): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/${cerereTypeId}/functie-assignments/${assignmentId}`;
        return this.http.delete(url, this.httpOptions);
    }

    /**
     * Reorder functie assignments
     */
    reorderFunctieAssignments(cerereTypeId: number, assignmentIds: number[]): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/${cerereTypeId}/functie-assignments/reorder`;
        return this.http.put(url, { assignment_ids: assignmentIds }, this.httpOptions);
    }

    /**
     * Get available users filtered by organigrama
     */
    getAvailableUsers(cerereTypeId: number, directieId: number, compartimentId?: number): Observable<any> {
        let url = `${this.basePath}api/nomenclatoare/cerere-type/${cerereTypeId}/available-users/${directieId}`;
        if (compartimentId) {
            url += `/${compartimentId}`;
        }
        return this.http.get(url, this.httpOptions);
    }

    /**
     * Get available functii
     */
    getAvailableFunctii(): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/available-functii`;
        return this.http.get(url, this.httpOptions);
    }

    /**
     * Get available directii with compartimente
     */
    getAvailableDirectii(): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/available-directii`;
        return this.http.get(url, this.httpOptions);
    }

    /**
     * Get compartimente for a specific directie
     */
    getCompartimenteByDirectie(directieId: number): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/available-directii/${directieId}/compartimente`;
        return this.http.get(url, this.httpOptions);
    }

    /**
     * Get assignment statistics for a cerere type
     */
    getAssignmentStatistics(cerereTypeId: number): Observable<any> {
        const url = `${this.basePath}api/nomenclatoare/cerere-type/${cerereTypeId}/assignment-statistics`;
        return this.http.get(url, this.httpOptions);
    }
}
